import React, { useCallback, useEffect, useState } from 'react';
import classnames from 'clsx';
import { FormattedMessage } from 'react-intl';
/** @typedef {import('react-intl').MessageDescriptor} MessageDescriptor */
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { useWizard } from 'ui-components/components/wizard/context';
import { noop } from 'ui-components/default-props/noop';

import { useDisabledButtonMessage } from './use-disabled-button-message';
import getDisplayInformation from '../../../../../../components/data-driven-logo/get-display-information';
import { useFeatureFlag } from '../../../../../../feature-flags/hooks/use-feature-flag';
import { FeatureFlagDeclarations } from '../../../../../../feature-flags/feature-flag-declarations';
import { useFsuiContext } from '../../../../../../financial-services-ui/fsui-context-provider';
import { useTaxFormContext } from '../../../../../../pages/settings/payment-and-subscription/context/tax-form-context-provider';
import { useShouldAutoInsure } from '../../../../../../services/insurance-service';
import Global from '../../../../../../store/global-selectors';
import { postWalletRegistrationSuccess } from '../../../../../../store/wallet/wallet.actions';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../../../../tracking/tracking-constants';
import { messages } from '../../../../../messages';
import { showProviderApprovalRequestedNotification } from '../../../../../messages/wallet-notifications';
import { trackWalletInsureAll } from '../../../../../tracking-events';
import { PAYMENT_TAB, STAMPS_BAD_CREDENTIALS_REGEX } from '../../../../../utilities/constants';
import { useWalletRegistrationErrorContext } from '../../../contexts/wallet-registration-error-context';
import { useWalletRegistrationFormContext } from '../../../contexts/wallet-registration-form-context';
import { useWalletRegistrationModalContext } from '../../../contexts/wallet-registration-modal-context';
import { usePrepareSubmissionData } from '../../../hooks/use-prepare-submission-data';
import style from '../style.module.less';

const reduceProviderResponsesToRegistrationsAndErrors = (registeredAndErrored, { labelProviderId, sellerLabelProviderId, error }) => {
  const errors = error?.Errors || [];
  const contractErrors = error?.statusResponse?.requestContractValidationErrors || [];

  return {
    registeredProviders: {
      labelProviderId,
      sellerLabelProviderId,
      name: getDisplayInformation({
        providerId: Number.parseInt(labelProviderId, 10),
      }),
    },
    combinedErrors: [...registeredAndErrored.combinedErrors, ...errors, ...contractErrors],
  };
};

/**
 * @param {object} props
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.isUpdate]
 * @param {() => void} [props.setWasTermsStepSuccessful]
 * @param {MessageDescriptor} [props.buttonMessageDescriptor]
 * @param {string} [props.buttonClassName]
 * @returns {React.ReactElement}
 */
export const SubmitButton = ({
  disabled = true,
  isUpdate = false,
  setWasTermsStepSuccessful = noop,
  buttonMessageDescriptor = messages.finishSetup,
  buttonClassName = undefined,
}) => {
  const enableWalletRegistrationErrorNotification = useFeatureFlag(FeatureFlagDeclarations.enableWalletRegistrationErrorNotification);
  const { jumpToStep, canProgress, stepForward } = useWizard();
  const dispatch = useDispatch();
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const {
    onSuccess: contextOnSuccess,
    labelPurchaseCallback,
    closeWalletModal,
    walletToRegister,
    shouldAutoInsure,
    isTermsAvailable,
    shouldShowAdyenDropInComponent,
  } = useWalletRegistrationModalContext();
  const { setErrors } = useWalletRegistrationErrorContext();
  const {
    submitRegistration,
    postWalletRegistrationSuccessPayload,
    isVerifiedAddress,
    hasSubmittedPayment,
    setHasSubmittedPayment,
    isPaymentComplete,
  } = useWalletRegistrationFormContext();
  const disabledButtonMessage = useDisabledButtonMessage();
  const { newInsuranceProviderIds = [] } = walletToRegister ?? {};
  const { setShouldAutoInsure: setShouldAutoInsureSellerSetting } = useShouldAutoInsure(newInsuranceProviderIds[0]);
  const trackWalletRegistration = useTrackWorkflow(Workflows.WALLET_REGISTRATION_FLOW);
  const { createPaymentSession } = useFsuiContext();
  const { prepareSubmissionData } = usePrepareSubmissionData();
  const [isFinalizingPayment, setIsFinalizingPayment] = useState(false);
  const { isEditing: isEditingTaxForm, submitTaxForm, isTaxSectionOpen, isInvalidForm: isInvalidTaxForm } = useTaxFormContext();
  const isInvalidSubmit = shouldShowAdyenDropInComponent && !isTermsAvailable && !isUpdate;

  const onFailure = useCallback(
    (errors) => {
      // TODO: handle backend validation errors
      const { Level = '', Message = 'unknown error', Code = '' } = errors[0] || {};
      const walletId = errors[0]?.ErrorContext?.metaData?.walletId || '';

      // NOTE: we refresh providers and wallets here as there is a scenario where registration was attempted and
      // was partially successful. A seller could have a wallet (or some seller providers) provisioned.
      // If that is the case, we want to update the modal to reflect the new state of the data, which means
      // updating entities to contain whatever new wallets or seller providers might have been created.
      dispatch(postWalletRegistrationSuccess(postWalletRegistrationSuccessPayload));
      setErrors(errors);
      trackWalletRegistration(WorkflowSteps.ERROR, {
        errors,
        Level,
        Message,
        Code,
        walletId,
      });
      if (Message.match(STAMPS_BAD_CREDENTIALS_REGEX)) {
        return jumpToStep(PAYMENT_TAB);
      }

      if (shouldShowAdyenDropInComponent) {
        // TODO: verify this works as expected, auctanePaySessionId might get out of sync
        createPaymentSession(true); // regenerate session because these are one-time use. They might have succeeded, so we need a new session.
      }
      jumpToStep(PAYMENT_TAB);
    },
    [
      dispatch,
      postWalletRegistrationSuccessPayload,
      setErrors,
      trackWalletRegistration,
      shouldShowAdyenDropInComponent,
      jumpToStep,
      createPaymentSession,
    ],
  );

  const continueRegistrationLogic = useCallback(async () => {
    setErrors([]);

    const { submissionData, allLabelProviderIds, nonDefaultResourceRequested } = prepareSubmissionData();
    const { results = {}, errors } = await submitRegistration(submissionData, isUpdate);

    if (errors) {
      return onFailure(errors);
    }
    const { walletId, labelProviderRegistrationResponses } = results || {};
    const { combinedErrors } = labelProviderRegistrationResponses.reduce(reduceProviderResponsesToRegistrationsAndErrors, {
      registeredProviders: [],
      combinedErrors: [],
    });

    if (enableWalletRegistrationErrorNotification && combinedErrors.length) {
      return setErrors(combinedErrors);
    }

    if (isEditingTaxForm && isTaxSectionOpen) {
      submitTaxForm();
    }

    trackWalletRegistration(WorkflowSteps.COMPLETE, {
      allLabelProviderIds,
      walletId,
      isUpdate,
      isVerifiedAddress,
    });
    contextOnSuccess(results);

    if (nonDefaultResourceRequested.length > 0) {
      showProviderApprovalRequestedNotification(nonDefaultResourceRequested, sellerHomeCountry);
    }

    /*
      This component is used as the primary action button for the terms step of the registration modal and the only button
      of the update modal. We prompt the user to insure all labels during the add funds step of the registration and in
      the update modal body. During update, this component needs to do the work to check and record the insure-all
      setting. During registration, this component needs to prepare the registration-only insure-all default of 'true'
      (`setShouldAutoInsure(true)`) before transitioning to the add funds step in `stepForward`
      */
    if (isUpdate) {
      trackWalletInsureAll(walletId, isUpdate, shouldAutoInsure);
      if (shouldAutoInsure) {
        await setShouldAutoInsureSellerSetting(true);
      }
    }

    if (!canProgress) {
      closeWalletModal();
      labelPurchaseCallback();
      // Refreshes redux cache of wallet
      return dispatch(postWalletRegistrationSuccess());
    }

    // If add funds step is available, do not refresh redux cache of wallet.isSetupComplete which controls add funds step
    setWasTermsStepSuccessful(true);
    stepForward();
  }, [
    setErrors,
    prepareSubmissionData,
    submitRegistration,
    isUpdate,
    onFailure,
    enableWalletRegistrationErrorNotification,
    isEditingTaxForm,
    isTaxSectionOpen,
    submitTaxForm,
    trackWalletRegistration,
    isVerifiedAddress,
    contextOnSuccess,
    sellerHomeCountry,
    shouldAutoInsure,
    setShouldAutoInsureSellerSetting,
    canProgress,
    closeWalletModal,
    labelPurchaseCallback,
    dispatch,
    setWasTermsStepSuccessful,
    stepForward,
  ]);

  const onClick = useCallback(async () => {
    if (isTaxSectionOpen && isInvalidTaxForm()) {
      return;
    }

    if (isInvalidSubmit) {
      setHasSubmittedPayment(true);
    } else {
      await continueRegistrationLogic();
    }
  }, [isTaxSectionOpen, isInvalidTaxForm, isInvalidSubmit, setHasSubmittedPayment, continueRegistrationLogic]);

  useEffect(() => {
    if (isPaymentComplete && shouldShowAdyenDropInComponent && !isTermsAvailable && !isUpdate) {
      setHasSubmittedPayment(false);
      setIsFinalizingPayment(true);
      // waiting two seconds for adyen drop in component to play the authorized animation before proceeding.
      const timeoutId = setTimeout(() => {
        setIsFinalizingPayment(false);
        continueRegistrationLogic();
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [isPaymentComplete, shouldShowAdyenDropInComponent, isTermsAvailable, isUpdate, continueRegistrationLogic, setHasSubmittedPayment]);

  const isAdyenActiveAndProcessing = shouldShowAdyenDropInComponent && (isFinalizingPayment || hasSubmittedPayment);
  const isDisabled = disabled || isAdyenActiveAndProcessing;

  return (
    <>
      <Button
        className={classnames(style.button, buttonClassName)}
        disabled={isDisabled}
        disabledTooltipMessage={disabledButtonMessage}
        onClick={onClick}
        theme='primary'
      >
        <FormattedMessage {...buttonMessageDescriptor} />
      </Button>
    </>
  );
};
